import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { getCurrentUser } from '../lib/auth.ts';
import { useToast } from '../components/ui/Toast.tsx';
import { BuilderToolbar, type DeviceWidth } from '../components/cms/VisualBuilder/BuilderToolbar.tsx';
import { BuilderIframe } from '../components/cms/VisualBuilder/BuilderIframe.tsx';
import { BlockPropsPanel } from '../components/cms/VisualBuilder/BlockPropsPanel.tsx';
import { BlockCatalogModal } from '../components/cms/VisualBuilder/BlockCatalogModal.tsx';
import { useBuilderMessages } from '../hooks/useBuilderMessages.ts';
import { useBuilderHistory } from '../hooks/useBuilderHistory.ts';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsBlock {
  id: string;
  blockDefinitionId: string | null;
  sortOrder: number;
  props: Record<string, unknown>;
  parentBlockId?: string | null;
  loyaltyMembersOnly?: boolean;
}

interface CmsPage {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'published';
  blocks: CmsBlock[];
}

interface BlockDef {
  id: string;
  name: string;
  slug: string;
  category: string;
  fieldsJson: { key: string; label: string; type: string; required?: boolean; options?: string[] }[];
}

// ─── Website URL ───────────────────────────────────────────────────────────────

const WEBSITE_BASE_DOMAIN = import.meta.env.VITE_WEBSITE_BASE_DOMAIN as string | undefined;

function buildWebsiteOrigin(spaceSlug?: string, tenantSlug?: string): string {
  if (WEBSITE_BASE_DOMAIN && spaceSlug && tenantSlug) {
    return `https://${spaceSlug}--${tenantSlug}.${WEBSITE_BASE_DOMAIN}`;
  }
  return (import.meta.env.VITE_WEBSITE_URL as string | undefined) ?? 'http://localhost:5175';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CmsVisualBuilderPage() {
  const { spaceId, pageId } = useParams<{ spaceId: string; pageId: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isIframeReady, setIsIframeReady] = useState(false);
  const [deviceWidth, setDeviceWidth] = useState<DeviceWidth>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Selected block state
  const [selectedBlock, setSelectedBlock] = useState<{
    id: string;
    type: string;
    defId: string | null;
    props: Record<string, unknown>;
  } | null>(null);

  // Local blocks state (source of truth in builder)
  const [blocks, setBlocks] = useState<CmsBlock[]>([]);

  // Block catalog modal states
  const [addToColumn, setAddToColumn] = useState<{ parentBlockId: string; column: number } | null>(null);
  const [showBlockCatalog, setShowBlockCatalog] = useState(false);

  // Undo/redo history
  const history = useBuilderHistory(blocks);

  // ── Resolve website origin for iframe ──────────────────────────────────────
  const user = getCurrentUser();
  const { data: space } = useQuery<{ slug: string }>({
    queryKey: ['cms-space', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}`).then((r) => r.data as { slug: string }),
    enabled: !!spaceId,
  });
  const websiteOrigin = buildWebsiteOrigin(space?.slug, user?.tenantSlug);

  // ── Data fetching ───────────────────────────────────────────────────────────

  const { data: page } = useQuery<CmsPage>({
    queryKey: ['cms-page', spaceId, pageId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/pages/${pageId}`).then((r) => r.data as CmsPage),
    enabled: !!spaceId && !!pageId,
  });

  const { data: blockDefs = [] } = useQuery<BlockDef[]>({
    queryKey: ['cms-block-definitions'],
    queryFn: () => api.get('/api/v1/cms/block-definitions').then((r) => r.data as BlockDef[]),
  });

  const { data: previewToken } = useQuery<string>({
    queryKey: ['cms-preview-token', spaceId, pageId],
    queryFn: async () => {
      const r = await api.post(
        `/api/v1/cms/spaces/${spaceId}/pages/${pageId}/preview-token`,
        {},
      );
      return (r.data as { token: string }).token;
    },
    enabled: !!spaceId && !!pageId,
    staleTime: 50 * 60 * 1000, // 50 minutes (token TTL is 1h)
  });

  // ── Init blocks from page data ──────────────────────────────────────────────

  useEffect(() => {
    if (page?.blocks && blocks.length === 0) {
      const sorted = [...page.blocks].sort((a, b) => a.sortOrder - b.sortOrder);
      setBlocks(sorted);
      history.reset(sorted);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── postMessage handling ────────────────────────────────────────────────────

  const handleIframeMessage = useCallback(
    (msg: import('../hooks/useBuilderMessages.ts').FromIframeMessage) => {
      switch (msg.type) {
        case 'READY':
          setIsIframeReady(true);
          break;
        case 'BLOCK_SELECT':
          setSelectedBlock({
            id: msg.blockId,
            type: msg.blockType,
            defId: msg.blockDefId,
            props: msg.props,
          });
          break;
        case 'BLOCK_REORDER': {
          // Update sortOrder for reordered root blocks, keep child blocks unchanged
          const orderMap = new Map(msg.orderedBlockIds.map((id, i) => [id, i]));
          const updated = blocks.map((b) => {
            const newOrder = orderMap.get(b.id);
            return newOrder != null ? { ...b, sortOrder: newOrder } : b;
          });
          setBlocks(updated);
          history.push(updated);
          setIsDirty(true);
          syncBlocksToIframe(updated);
          break;
        }
        case 'ADD_BLOCK_TO_COLUMN':
          setAddToColumn({ parentBlockId: msg.parentBlockId, column: msg.column });
          setSelectedBlock(null);
          break;
        case 'ADD_BLOCK_ROOT':
          setShowBlockCatalog(true);
          setSelectedBlock(null);
          break;
        case 'BLOCK_DELETE': {
          const updated = blocks.filter((b) => b.id !== msg.blockId && b.parentBlockId !== msg.blockId);
          setBlocks(updated);
          history.push(updated);
          setIsDirty(true);
          syncBlocksToIframe(updated);
          if (selectedBlock?.id === msg.blockId) setSelectedBlock(null);
          break;
        }
        case 'BLOCK_MOVE': {
          const block = blocks.find((b) => b.id === msg.blockId);
          if (!block) break;
          // Find siblings (same parent, same column for nested blocks)
          const siblings = blocks
            .filter((b) => b.parentBlockId === block.parentBlockId)
            .filter((b) => block.parentBlockId == null || (b.props as any)?._col === (block.props as any)?._col)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          const idx = siblings.findIndex((s) => s.id === block.id);
          const swapIdx = msg.direction === 'up' ? idx - 1 : idx + 1;
          if (swapIdx < 0 || swapIdx >= siblings.length) break;
          // Swap sortOrder
          const updated = blocks.map((b) => {
            if (b.id === siblings[idx].id) return { ...b, sortOrder: siblings[swapIdx].sortOrder };
            if (b.id === siblings[swapIdx].id) return { ...b, sortOrder: siblings[idx].sortOrder };
            return b;
          });
          setBlocks(updated);
          history.push(updated);
          setIsDirty(true);
          syncBlocksToIframe(updated);
          break;
        }
      }
    },
    [blocks, history, selectedBlock],
  );

  const { sendToIframe } = useBuilderMessages({
    iframeRef,
    iframeOrigin: websiteOrigin,
    onMessage: handleIframeMessage,
  });

  // ── Send blocks to iframe when ready or blocks change ──────────────────────

  // Build a map from blockDefinitionId → slug for the iframe's BlockRenderer
  const defIdToSlug = new Map(blockDefs.map((d) => [d.id, d.slug]));

  // Blocks that can be nested inside containers (exclude layout blocks)
  const LAYOUT_SLUGS = new Set(['row', 'spacer']);
  const nestableBlockDefs = blockDefs.filter((d) => !LAYOUT_SLUGS.has(d.slug));

  interface EnrichedBlock {
    id: string;
    type: string;
    sortOrder: number;
    props: Record<string, unknown>;
    blockDefinitionId: string | null;
    parentBlockId?: string | null;
    loyaltyMembersOnly?: boolean;
    children: EnrichedBlock[];
  }

  function enrichBlock(b: CmsBlock): EnrichedBlock {
    return {
      id: b.id,
      type: b.blockDefinitionId ? (defIdToSlug.get(b.blockDefinitionId) ?? 'unknown') : 'unknown',
      sortOrder: b.sortOrder,
      props: b.props,
      blockDefinitionId: b.blockDefinitionId,
      parentBlockId: b.parentBlockId,
      loyaltyMembersOnly: b.loyaltyMembersOnly,
      children: [],
    };
  }

  /** Enrich flat blocks → nested tree with `type` slugs and `children[]` for the iframe. */
  function buildTreeForIframe(raw: CmsBlock[]): EnrichedBlock[] {
    const enriched = raw.map(enrichBlock);
    const byId = new Map(enriched.map((b) => [b.id, b]));
    const roots: EnrichedBlock[] = [];
    for (const b of enriched) {
      if (b.parentBlockId && byId.has(b.parentBlockId)) {
        byId.get(b.parentBlockId)!.children.push(b);
      } else {
        roots.push(b);
      }
    }
    roots.sort((a, b) => a.sortOrder - b.sortOrder);
    for (const node of byId.values()) node.children.sort((x: EnrichedBlock, y: EnrichedBlock) => x.sortOrder - y.sortOrder);
    return roots;
  }

  /** Send current blocks to iframe as nested tree. */
  function syncBlocksToIframe(currentBlocks?: CmsBlock[]) {
    const src = currentBlocks ?? blocks;
    if (src.length === 0 || blockDefs.length === 0) return;
    const tree = buildTreeForIframe(src);
    sendToIframe({ type: 'BLOCK_TREE', blocks: tree });
  }

  useEffect(() => {
    if (!isIframeReady || blocks.length === 0 || blockDefs.length === 0) return;
    syncBlocksToIframe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIframeReady, blocks, blockDefs]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // ── Block operations ────────────────────────────────────────────────────────

  function handleBlockUpdate(blockId: string, newProps: Record<string, unknown>) {
    const updated = blocks.map((b) => (b.id === blockId ? { ...b, props: newProps } : b));
    setBlocks(updated);
    setSelectedBlock((prev) => (prev?.id === blockId ? { ...prev, props: newProps } : prev));
    sendToIframe({ type: 'BLOCK_UPDATE', blockId, props: newProps });
    setIsDirty(true);
    history.push(updated);
  }

  function handleBlockDelete(blockId: string) {
    const next = blocks.filter((b) => b.id !== blockId);
    setBlocks(next);
    sendToIframe({ type: 'BLOCK_DELETE', blockId });
    setSelectedBlock(null);
    setIsDirty(true);
    history.push(next);
  }

  function handleUndo() {
    const prev = history.undo();
    if (!prev) return;
    setBlocks(prev);
    syncBlocksToIframe(prev);
    setIsDirty(true);
  }

  function handleRedo() {
    const next = history.redo();
    if (!next) return;
    setBlocks(next);
    syncBlocksToIframe(next);
    setIsDirty(true);
  }

  // ── Add block (supports nesting via parentBlockId + column) ─────────────────

  function handleAddBlock(defSlug: string, parentBlockId?: string | null, column?: number) {
    const def = blockDefs.find((d) => d.slug === defSlug);
    const siblings = blocks.filter((b) =>
      parentBlockId ? b.parentBlockId === parentBlockId : !b.parentBlockId,
    );
    const newBlock: CmsBlock = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      blockDefinitionId: def?.id ?? null,
      parentBlockId: parentBlockId ?? null,
      sortOrder: siblings.length,
      props: column != null ? { _col: column } : {},
      loyaltyMembersOnly: false,
    };
    const updated = [...blocks, newBlock];
    setBlocks(updated);
    history.push(updated);
    setIsDirty(true);
    syncBlocksToIframe(updated);
    // Close the column picker if open
    if (addToColumn) setAddToColumn(null);
  }

  // ── Save ───────────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/api/v1/cms/spaces/${spaceId}/pages/${pageId}/blocks`, {
        blocks: blocks.map(({ id, blockDefinitionId, sortOrder, props, parentBlockId, loyaltyMembersOnly }) => ({
          id,
          blockDefinitionId,
          sortOrder,
          props,
          parentBlockId: parentBlockId ?? null,
          loyaltyMembersOnly: loyaltyMembersOnly ?? false,
        })),
      });
      // Refetch to get real IDs for newly created blocks
      const r = await api.get(`/api/v1/cms/spaces/${spaceId}/pages/${pageId}`);
      return (r.data as CmsPage).blocks;
    },
    onSuccess: (savedBlocks) => {
      const sorted = [...savedBlocks].sort((a, b) => a.sortOrder - b.sortOrder);
      setBlocks(sorted);
      history.push(sorted);
      setIsDirty(false);
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      toast.success('Page saved');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save')),
  });

  // ── Publish ────────────────────────────────────────────────────────────────

  const publishMutation = useMutation({
    mutationFn: async () => {
      // Save first if dirty
      if (isDirty) {
        await saveMutation.mutateAsync();
      }
      return api.post(`/api/v1/cms/spaces/${spaceId}/pages/${pageId}/publish`, {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      toast.success('Page published');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to publish')),
  });

  // ── Build iframe URL ───────────────────────────────────────────────────────

  const iframeSrc =
    page && previewToken
      ? `${websiteOrigin}/?editMode=true&previewToken=${previewToken}&pageSlug=${encodeURIComponent(page.slug)}`
      : null;

  // ── Find selected block def ────────────────────────────────────────────────

  const selectedBlockDef = selectedBlock
    ? (blockDefs.find((d) => d.id === selectedBlock.defId || d.slug === selectedBlock.type) ??
      null)
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white">
      <BuilderToolbar
        spaceId={spaceId ?? ''}
        pageTitle={page?.title ?? '…'}
        pageStatus={page?.status ?? 'draft'}
        isDirty={isDirty}
        isSaving={saveMutation.isPending}
        isPublishing={publishMutation.isPending}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        deviceWidth={deviceWidth}
        onDeviceChange={setDeviceWidth}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onSave={() => saveMutation.mutate()}
        onPublish={() => publishMutation.mutate()}
      />

      <div className="flex flex-1 overflow-hidden">
        {iframeSrc ? (
          <BuilderIframe
            ref={iframeRef}
            src={iframeSrc}
            deviceWidth={deviceWidth}
            isReady={isIframeReady}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center text-gray-500 text-sm">
            <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
            </svg>
            Loading…
          </div>
        )}

        {selectedBlock && !addToColumn && (
          <BlockPropsPanel
            blockId={selectedBlock.id}
            blockType={selectedBlock.type}
            blockDef={selectedBlockDef}
            props={selectedBlock.props}
            spaceId={spaceId}
            onUpdate={handleBlockUpdate}
            onDelete={handleBlockDelete}
            onClose={() => setSelectedBlock(null)}
          />
        )}

        {/* Modal block picker for adding to a column */}
        {addToColumn && (
          <BlockCatalogModal
            title={`Add block to Column ${addToColumn.column + 1}`}
            blockDefs={nestableBlockDefs}
            onAddBlock={(slug) => {
              handleAddBlock(slug, addToColumn.parentBlockId, addToColumn.column);
              setAddToColumn(null);
            }}
            onClose={() => setAddToColumn(null)}
          />
        )}

        {/* Modal block picker for adding root blocks (triggered from iframe "+" button) */}
        {showBlockCatalog && (
          <BlockCatalogModal
            title="Add block"
            blockDefs={blockDefs}
            onAddBlock={(slug) => {
              handleAddBlock(slug);
              setShowBlockCatalog(false);
            }}
            onClose={() => setShowBlockCatalog(false)}
          />
        )}
      </div>
    </div>
  );
}
